import { useState } from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

// Import AuthProvider
import { AuthProvider } from "./context/AuthContext"

// Front Page
import SchoolFrontPage from "./frontpage/HomePage";

import Login from "./account/Login";
import Dashboard from "./main/Dashboard";
import UserManagement from "./users/UserPage";
import UserInfo from "./users/UserInfo";
import UnifiedLeaveDashboard from "./employee/leaves/EmployeeLeavePage";
import CheckAttendance from "./employee/attendances/CheckAttendance";
import AttendanceReports from "./employee/attendances/AttendanceReports";

// Student
import StudentManager from "./student/StudentManager";
import UniformInfractionPage from "./student/pages/UniformInfraction";
import AbsenceRequestForm from "./student/pages/AbsentRequestForm";

// Personal Profile
import ProfilePage from "./personal_account/ProfilePage";
import MyLeave from "./me/pages/MyLeave";
import MyRoom from "./me/pages/MyRoom";

// Academic
import AcademicManager from "./academic/AcademicManager";
import SetupYearTerm from "./academic/SetupYearTerm";
import RegistrationForm from "./registrar/pages/RegistrationForm";
import RegistrarDashboard from "./registrar/pages/RegistrarDashboard";

// Employee
import EmployeeManager from "./employee/EmployeeManager";

// Teacher
import TeacherDashboard from "./teacher/TeacherDashboard";
import TeacherClassroom from "./teacher/pages/TeacherClassroom";
import TeacherStudentList from "./teacher/pages/TeacherStudentList";
import TeacherStudentDetail from "./teacher/pages/TeacherStudentDetail";

// Inventory
import InventoryControl from "./inventory/InventoryControl";
import NetworkEquipement from "./inventory/pages/NetworkEquipment";
import DesktopComputer from "./inventory/pages/DesktopComputer";
import CCTVCamera from "./inventory/pages/CCTVCamera";
import Projectors from "./inventory/pages/Projectors";
import MediaEquipment from "./inventory/pages/MediaEquipment";
import ClassroomEquipment from "./inventory/pages/ClassroomEquipment";
import RoomEquipment from "./inventory/pages/RoomEquipment";
import RoomInfo from "./inventory/pages/RoomInfo";

// Library
import Catalog from "./library/Catalog";
import Cataloging from "./library/pages/Cataloging";
import ViewBook from "./library/pages/ViewBook";
import Patrons from "./library/pages/Patrons";
import ViewPatron from "./library/pages/ViewPatron";
import CirculationPage from "./library/pages/Circulation";
import FineDashboard from "./library/pages/PayFine";
import Reports from "./library/pages/Report";

// Configuration
import ConfigurationPage from "./setting/SettingPage";

// Report Manager
import ReportManager from "./report_manager/ReportManager";

// Classroom Manager
import ClassroomManager from "./classroom/ClassroomManager";

// Reset Password
import ForgotPassword from "./account/components/ForgotPassword";
import ResetConfirm from "./account/components/ResetConfirm";

// Lock Account
import LockedAccounts from "./account/LockedAccounts";

// ---------- ProtectedRoute ----------
function ProtectedRoute({ children, token }) {
  const location = useLocation();
  if (!token) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

// ---------- PermissionRoute ----------
function PermissionRoute({ children, requiredCodename }) {
  const perms = JSON.parse(sessionStorage.getItem("permissions") || "[]");
  if (!perms.includes(requiredCodename)) return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeRedirect() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const userGroups = user.groups || [];
  
  if (user.group_name === "Faculty" || userGroups.includes("Faculty")) {
    return <Navigate to="/teacher/dashboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// ---------- App Component ----------
function App() {
  const [token, setToken] = useState(sessionStorage.getItem("access_token") || null);

  const handleLogout = () => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("permissions");
    sessionStorage.removeItem("user");
    setToken(null);
  };

  return (
    <>
      <AuthProvider>
        <Router>
          <Routes>

            {/* Home / Login */}
            <Route
              path="/"
              element={token ? <HomeRedirect /> : <Login setToken={setToken} />}
            />
            
            {/* Reset Password */}
            <Route path="/reset-password" element={<ForgotPassword />} />
            <Route path="/password-reset-confirm/:uid/:token/" element={<ResetConfirm />} />

            <Route
              path="/registration/form"
              element={<RegistrationForm />}
            />

            <Route
              path="students/absent/form"
              element={<AbsenceRequestForm />}
            />

            {/* Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute token={token}>
                  <Dashboard handleLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Users */}
            <Route
              path="/users"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_user_list">
                    <UserManagement />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_user_info">
                    <UserInfo />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />



            {/* Start Academic */}
            <Route
              path="/academic"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_academic_page">
                    <AcademicManager />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* End Academic */}

            {/* Start Employees */}
            <Route
              path="/employees"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_employee_page">
                    <EmployeeManager />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            {/* End Employees */}

            <Route
              path="/academic/set/year&term"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_setup_year_term">
                    <SetupYearTerm />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Start Students */}
            <Route
              path="/students"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_student_page">
                    <StudentManager />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/students/uniform"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_uniform">
                    <UniformInfractionPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            {/* End Students */}

            {/* Attendance */}
            <Route
              path="/attendance"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_attendance">
                    <CheckAttendance />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Attendance Reports */}
            <Route
              path="/attendance/reports"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_attendance">
                    <AttendanceReports />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Leaves */}
            <Route
              path="/leave"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_leave">
                    <UnifiedLeaveDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Inventory */}
            <Route
              path="/inventory"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_inventory">
                    <InventoryControl />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/room-equipment"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_room_equipment">
                    <RoomEquipment />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/room-equipment/:id"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_room_info">
                    <RoomInfo />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/network-equipment"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_network_equipment">
                    <NetworkEquipement />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/desktop-computer"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_desktop_computer">
                    <DesktopComputer />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/cctv-camera"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_cctv_camera">
                    <CCTVCamera />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/projectors"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_projectors">
                    <Projectors />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/media-equipment"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_media_equipment">
                    <MediaEquipment />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/classroom-equipment"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_classroom_equipment">
                    <ClassroomEquipment />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Library */}
            <Route
              path="/library"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <Catalog />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/cataloging"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <Cataloging />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/cataloging/book/detail"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <ViewBook />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/cataloging/book/detail/:id"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <ViewBook />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/patrons"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <Patrons />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/patrons/detail"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <ViewPatron />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/patrons/detail/:id"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <ViewPatron />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/circulation"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <CirculationPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/payfine"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_payfine">
                    <FineDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/reports"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_library">
                    <Reports />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            
            {/* Registrar */}
            <Route
              path="/registrar"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_new_student_registration">
                    <RegistrarDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Configuration */}
            <Route
              path="/configuration"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_configure_system">
                    <ConfigurationPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Teacher */}
            <Route
              path="/teacher/dashboard"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="teacher_dashboard">
                    <TeacherDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/teacher/classroom"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="teacher_classroom">
                    <TeacherClassroom />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/teacher/classroom/:subjectId"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="teacher_classroom">
                    <TeacherClassroom />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/teacher/student/list"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="teacher_can_view_student_list">
                    <TeacherStudentList />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/teacher/student/:id"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="teacher_can_view_student_detail">
                    <TeacherStudentDetail />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/report_manager/"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_report_manager">
                    <ReportManager />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/classroom_manager/"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_admin_classroom">
                    <ClassroomManager />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ME */}
            <Route
              path="/my/leave"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_my_leave">
                    <MyLeave />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/my/room"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_my_room">
                    <MyRoom />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Profile Page */}
            <Route
              path="/my/profile"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_profile">
                    <ProfilePage handleLogout={handleLogout}/>
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Reset Password */}
            <Route
              path="/account/locked"
              element={
                <ProtectedRoute token={token}>
                  <PermissionRoute requiredCodename="can_view_account_lock">
                    <LockedAccounts />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </>
  );
}

export default App;